import React, { useCallback, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import classnames from 'classnames'

import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons'

import { LineItemCount } from '../components/LineItems'
import TrackingEvents from '../components/TrackingEvents'
import { BtLink } from '../components/BtLink'
import Icon from '../components/Icon'

import { CardSection } from './Card'
import LineItemsCard from './LineItemsCard'
import { LineItemCardClasses, LineItemClasses, TrackingClassses } from './ShipmentActivityContent'

interface TabbedClasses {
  root?: string
  lineItemsCard: string
  shipmentActivity?: string
  scrollableSection?: string
  marker?: string
  eventRow?: string
  shipmentTabs?: string
  tab?: string
  activeTab?: string
  inactiveTab?: string
  leftTab?: string
  rightTab?: string
}

export default function TabbedShipmentActivityContent({
  classes,
  trackingClasses,
  lineItemClasses,
  lineItemCardClasses,
  isGiftWrapped,
  lineItemsCardShown,
}: {
  classes: TabbedClasses
  trackingClasses: TrackingClassses
  lineItemClasses: LineItemClasses
  lineItemCardClasses: LineItemCardClasses
  isGiftWrapped: Boolean
  lineItemsCardShown: Boolean
}) {
  const intl = useIntl()

  const formatTimestamp = (timestamp: Date) => {
    const date = intl
      .formatDateToParts(timestamp, { day: 'numeric', month: 'long' })
      .map(p => p.value)
      .join('')
    const time = intl
      .formatDateToParts(timestamp, { hour: 'numeric', minute: 'numeric' })
      .map(p => p.value)
      .join('')
    return `${date} - ${time}`
  }

  const eventClassName = (index: number) => classnames(classes.eventRow, { mostRecentEvent: index === 0 })

  const [showActivity, setShowActivity] = useState(true)
  const toggleShowActivity = useCallback(() => setShowActivity(true), [])
  const toggleShowContent = useCallback(() => setShowActivity(false), [])

  return (
    <div className={classes.root}>
      <div className={classes.shipmentTabs}>
        <BtLink
          className={`${classes.tab} ${classes.leftTab} ${showActivity ? classes.activeTab : classes.inactiveTab} `}
          onClick={toggleShowActivity}>
          {isGiftWrapped ? (
            <FormattedMessage description="layout02.shipmentTabs.activityAsGift" defaultMessage="Gift Activity" />
          ) : (
            <FormattedMessage description="layout02.shipmentTabs.activity" defaultMessage="Shipment Activity" />
          )}
        </BtLink>
        {lineItemsCardShown && (
          <BtLink
            className={`${classes.tab} ${classes.rightTab} ${!showActivity ? classes.activeTab : classes.inactiveTab}`}
            onClick={toggleShowContent}>
            <FormattedMessage description="layout02.shipmentTabs.content" defaultMessage="Shipment Contents" />
            {' ('}
            <LineItemCount />
            {')'}
          </BtLink>
        )}
      </div>
      <div className={classes.shipmentActivity}>
        {showActivity && (
          <div className={classes.scrollableSection} role="list">
            <TrackingEvents
              classes={trackingClasses}
              truncateToCount={3}
              formatTimestamp={formatTimestamp}
              eachEvent={({ content, key, index }) => (
                <CardSection className={eventClassName(index)} key={key}>
                  {index === 0 && <Icon icon={faMapMarkerAlt} className={classes.marker} />}
                  {content}
                </CardSection>
              )}
            />
          </div>
        )}
        {lineItemsCardShown && !showActivity && (
          <LineItemsCard
            className={classes.lineItemsCard}
            lineItemClasses={lineItemClasses}
            lineItemCardClasses={lineItemCardClasses}
          />
        )}
      </div>
    </div>
  )
}
